import React, { useState, useEffect, useRef, useCallback, useMemo, useReducer } from 'react';

// Enhanced game constants with better mobile deployment compatibility
const GAME_CONSTANTS = {
  GAME_WIDTH: 320,
  GAME_HEIGHT: 160,
  INITIAL_TIME: 60,
  BORDER_WIDTH: 2,
  RADIUS: 16,
  MOUSE_SPEED: 3,
  INITIAL_CAT_SPEED: 0.7,
  MAX_CAT_SPEED: 1.6,
  CAT_SPEED_INCREMENT: 0.0028,
  TIMER_THRESHOLD: 30,
  DISAPPEARING_CHEESE_DURATION: 2000,
  TARGET_FPS: 70,
  TIMER_INTERVAL: 1000,
  TARGET_CHEESE_MOBILE: 64,
  TARGET_CHEESE_DESKTOP: 72,
  CHEESE_CREATION_PROBABILITY: 0.6,
  DISAPPEARING_CHEESE_PROBABILITY: 0.25,
  CAT_RANDOM_MOVE_PROBABILITY: 0.02,
  MAX_CHEESE_COUNT: 2,
  MAX_PLACEMENT_ATTEMPTS: 15,
  CAT_SCALE_MOBILE: 2,
  PREDICTION_FACTOR: 0.3,
  CAT_ADVANCED_SPEED_MULTIPLIER: 1.1,
  CAT_RANDOM_SPEED_MULTIPLIER: 1.2,
  ENTITY_BOUNDARY_OFFSET: 15,
  ENTITY_BOUNDARY_SIZE: 20,
  ENTITY_BOUNDARY_LARGE: 40
};

// Optimized distance calculation functions for better performance
const calculateDistanceSquared = (pos1, pos2) => {
  const dx = pos1.x - pos2.x;
  const dy = pos1.y - pos2.y;
  return dx * dx + dy * dy; // Skip square root calculation for performance
};

const calculateDistance = (pos1, pos2) => {
  const dx = pos1.x - pos2.x;
  const dy = pos1.y - pos2.y;
  return Math.sqrt(dx * dx + dy * dy);
};

// Pre-calculated squared thresholds for collision detection optimization
const THRESHOLDS = {
  cheeseCollect: 25 * 25,        // 625 for cheese collection
  catCatch: 32 * 32,             // 1024 for cat catching mouse
  cheeseSpacing: 50 * 50,        // 2500 for cheese placement spacing
  mouseDistance: 40 * 40,        // 1600 for mouse proximity check
  catDistance: 40 * 40,          // 1600 for cat proximity check
  disappearingSpacing: 100 * 100 // 10000 for disappearing cheese spacing
};

// Utility function to clamp values within bounds
const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

// Enhanced DOM element position update for better mobile deployment performance
const updateNodePos = (ref, x, y, gameWidth, gameHeight, flipX = null, isCat = false) => {
  if (!ref.current) return;
  
  ref.current.style.left = `${(x / gameWidth) * 100}%`;
  ref.current.style.top = `${(y / gameHeight) * 100}%`;
  
  // Optimized transform handling for cat entities
  if (isCat) {
    return;
  }
  
  if (flipX !== null) {
    const scaleX = flipX ? -1 : 1;
    ref.current.style.transform = `translate(-50%, -50%) scaleX(${scaleX})`;
  } else {
    ref.current.style.transform = 'translate(-50%, -50%)';
  }
};

// Combined clamp and DOM update for optimized layout performance
const moveAndUpdateNode = (ref, x, y, gameWidth, gameHeight, flipX = null, isCat = false) => {
  const clampedX = clamp(x, GAME_CONSTANTS.ENTITY_BOUNDARY_OFFSET, gameWidth - GAME_CONSTANTS.ENTITY_BOUNDARY_OFFSET);
  const clampedY = clamp(y, GAME_CONSTANTS.ENTITY_BOUNDARY_OFFSET, gameHeight - GAME_CONSTANTS.ENTITY_BOUNDARY_OFFSET);
  
  updateNodePos(ref, clampedX, clampedY, gameWidth, gameHeight, flipX, isCat);
  
  return { x: clampedX, y: clampedY };
};

// Enhanced cat direction calculation based on movement vector
const getCatDirection = (moveX, moveY) => {
  if (Math.abs(moveX) > Math.abs(moveY)) {
    return moveX > 0 ? 'right' : 'left';
  } else {
    return moveY > 0 ? 'down' : 'up';
  }
};

// Enhanced position validation using squared distance for performance
const isValidPosition = (newPos, existingPositions, minDistancesSquared) => {
  for (let i = 0; i < existingPositions.length; i++) {
    const distSquared = calculateDistanceSquared(newPos, existingPositions[i]);
    if (distSquared < minDistancesSquared[i]) {
      return false;
    }
  }
  return true;
};

// Enhanced touch button base style to prevent browser defaults
const touchButtonBaseStyle = {
  outline: 'none',
  border: 'none',
  WebkitAppearance: 'none',
  MozAppearance: 'none',
  appearance: 'none',
  WebkitFocusRingColor: 'transparent',
  boxShadow: 'none',
  WebkitTapHighlightColor: 'transparent'
};

// Enhanced static style objects for better mobile deployment performance
const staticStyles = {
  gameContainer: {
    position: 'relative',
    background: 'linear-gradient(135deg, #86efac, #4ade80)',
    width: '100%',
    height: '100%'
  },
  gameOverlay: {
    position: 'absolute',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 20,
    borderRadius: 'inherit'
  },
  gameOverModal: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '16px',
    textAlign: 'center',
    margin: '0 16px',
    maxWidth: '300px'
  },
  backButton: {
    transform: 'translateY(-1.8px)'
  },
  startButton: {
    background: 'linear-gradient(135deg, #f97316, #ea580c)'
  },
  animatedBorder: {
    background: 'linear-gradient(135deg, #fbbf24, #f59e0b, #d97706, #b45309, #fbbf24, #f59e0b, #d97706, #fbbf24)',
    backgroundSize: '300% 300%',
    animation: 'cheeseBorderMove 2s linear infinite'
  },
  adBanner: {
    backgroundColor: 'rgba(156, 163, 175, 0.3)',
    border: '3px solid #10b981',
    borderRadius: '18px',
    padding: '20px',
    margin: '16px 0',
    backdropFilter: 'blur(12px)',
    textAlign: 'center',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1), 0 0 20px rgba(16, 185, 129, 0.3), 0 0 0 2px #374151',
  },
  adLogo: {
    width: '96px',
    height: '96px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 12px auto',
    fontSize: '30px',
    fontWeight: 'bold',
    color: '#374151',
    border: '2px solid #374151',
  },
  adSlogan: {
    color: '#1f2937',
    fontSize: '15px',
    fontWeight: '700',
    marginBottom: '8px',
    textShadow: '0 1px 2px rgba(255, 255, 255, 0.5)'
  },
  adLink: {
    color: '#ffffff',
    fontSize: '13px',
    textDecoration: 'underline',
    fontWeight: '600',
    textShadow: '1px 1px 0 #374151, -1px -1px 0 #374151, 1px -1px 0 #374151, -1px 1px 0 #374151'
  }
};

// Enhanced emoji constants for better consistency across deployments
const MOUSE_EMOJI = '🐀';
const CAT_EMOJIS = {
  left: '😼',
  right: '😾', 
  up: '😼',
  down: '😼'
};

const ARROW_EMOJIS = {
  up: '↑',
  down: '↓',
  left: '←', 
  right: '→'
};

// Enhanced header component with React.memo optimization
const Header = React.memo(({ 
  timeLeft, 
  totalCheeseCollected, 
  targetCheese, 
  setCurrentPage, 
  deviceSettings
}) => (
  <div className="text-center mb-4">
    <div className="flex justify-start mb-3">
      <button 
        onClick={() => setCurrentPage('landing')}
        className="bg-white/20 text-white px-3 py-2 rounded-lg hover:bg-white/30 transition-colors text-sm flex items-center justify-center gap-1 ml-9"
        style={touchButtonBaseStyle}
      >
        <span style={staticStyles.backButton}>←</span>
        <span>Home</span>
      </button>
    </div>
    <div className="text-lg font-bold text-white mb-2">
      <span>⏳</span> {timeLeft}s
    </div>
    
    <div className="relative inline-block">
      <div className={`border border-white/30 rounded-2xl px-4 py-2 shadow-lg ${
        deviceSettings.isMobile 
          ? 'bg-black/40' 
          : 'bg-black/20 backdrop-blur-sm'
      }`}>
        <div className={`flex items-center justify-center gap-4 text-[13px] font-semibold ${
          deviceSettings.isMobile ? '' : 'font-inter'
        }`}>
          <div className="flex items-center gap-1">
            <span className="text-amber-300">🧀</span>
            <span className="text-white">{totalCheeseCollected}</span>
          </div>
          <div className="w-px h-4 bg-white/40"></div>
          <div className="flex items-center gap-1">
            <span className="text-green-300">🎯</span>
            <span className="text-white/90">{targetCheese}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
), (prevProps, nextProps) => {
  return prevProps.timeLeft === nextProps.timeLeft && 
         prevProps.totalCheeseCollected === nextProps.totalCheeseCollected;
});

// Enhanced touch button component with better mobile deployment handling
const TouchButton = React.memo(({ direction, onPress, className = "" }) => {
  const handleEvent = useCallback((e, isPressed) => {
    e.preventDefault();
    e.stopPropagation();
    onPress(direction, isPressed);
  }, [direction, onPress]);

  const handleMouseLeave = useCallback(() => {
    onPress(direction, false);
  }, [direction, onPress]);

  return (
    <button
      className={`bg-orange-500 text-white text-2xl font-bold rounded-xl h-12 w-12 outline-none flex items-center justify-center ${className}`}
      style={touchButtonBaseStyle}
      tabIndex="-1"
      onFocus={(e) => e.target.blur()}
      onContextMenu={(e) => e.preventDefault()}
      onSelectStart={(e) => e.preventDefault()}
      onDragStart={(e) => e.preventDefault()}
      onTouchStart={(e) => handleEvent(e, true)}
      onTouchEnd={(e) => handleEvent(e, false)}
      onTouchMove={direction === 'down' ? (e) => e.preventDefault() : undefined}
      onMouseDown={(e) => handleEvent(e, true)}
      onMouseUp={(e) => handleEvent(e, false)}
      onMouseLeave={handleMouseLeave}
    >
      <span className="pointer-events-none leading-none -translate-y-[1px]">
        {ARROW_EMOJIS[direction]}
      </span>
    </button>
  );
});

// Enhanced landing page component with better mobile deployment support
const LandingPage = React.memo(({ setCurrentPage, targetCheese, isMobile }) => (
  <div className="min-h-screen bg-gradient-to-br from-green-400 via-green-500 to-green-600 flex items-center justify-center p-4" 
       style={{ minHeight: '100vh', minHeight: '-webkit-fill-available' }}>
    <style jsx>{`
      @keyframes cheeseBorderMove {
        0% {
          background-position: 100% 0%;
        }
        100% {
          background-position: 0% 100%;
        }
      }
    `}</style>
    
    <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 text-center max-w-md w-full shadow-2xl border border-white/20">
      <div className="text-6xl mb-4">🧀</div>
      <h1 className="text-3xl font-bold text-white mb-4">The Relentless Cheese Hunt</h1>
      <p className="text-white/90 mb-6 text-lg">
        Control the mouse to collect cheese and escape from the cat!
      </p>
      
      <div style={staticStyles.adBanner} className="mb-6">
        <div style={staticStyles.adLogo}>
          LOGO
        </div>
        <div style={staticStyles.adSlogan}>
          Your Company Slogan Here
        </div>
        <div style={staticStyles.adLink}>
          www.yourcompany.com
        </div>
      </div>
      
      <div className="relative inline-block mt-6">          
        <div 
          className="absolute -inset-1 rounded-2xl"
          style={staticStyles.animatedBorder}
        ></div>
        
        <button 
          onClick={() => setCurrentPage('game')}
          className="relative bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-8 rounded-2xl text-xl transition-all duration-300 transform hover:scale-105 shadow-lg z-10"
          style={{ ...staticStyles.startButton, ...touchButtonBaseStyle }}
        >
          🎮 Start Game
        </button>
      </div>
    </div>
  </div>
));

// Enhanced game state reducer for centralized state management
const gameReducer = (state, action) => {
  switch (action.type) {
    case 'MOVE_MOUSE':
      return {
        ...state,
        mousePos: action.mousePos,
        mouseDirection: action.direction
      };
    case 'MOVE_CAT':
      return {
        ...state,
        catPos: action.catPos,
        catDirection: action.direction
      };
    case 'UPDATE_CHEESES':
      return {
        ...state,
        cheeses: action.cheeses
      };
    case 'COLLECT_CHEESE':
      const newTotal = state.totalCheeseCollected + 1;
      return {
        ...state,
        cheeses: action.cheeses,
        cheeseCounter: state.cheeseCounter + 1,
        totalCheeseCollected: newTotal,
        ...(newTotal >= action.targetCheese ? { 
          gameOver: true, 
          gameRunning: false, 
          gameOverReason: 'won' 
        } : {})
      };
    case 'GAME_OVER':
      return {
        ...state,
        gameOver: true,
        gameRunning: false,
        gameOverReason: action.reason
      };
    case 'TIMER_TICK':
      return {
        ...state,
        timeLeft: action.timeLeft
      };
    case 'UPDATE_CAT_SPEED':
      return {
        ...state,
        catSpeed: action.catSpeed,
        cheeseCounter: 0
      };
    case 'UPDATE_CAT_SCALE':
      return {
        ...state,
        catScale: action.catScale
      };
    case 'RESTART_GAME':
      return {
        timeLeft: GAME_CONSTANTS.INITIAL_TIME,
        gameOver: false,
        gameRunning: true,
        gameOverReason: '',
        mousePos: { x: 50, y: 40 },
        catPos: { x: 280, y: 80 },
        cheeses: [],
        mouseDirection: 'right',
        catDirection: 'left',
        cheeseCounter: 0,
        totalCheeseCollected: 0,
        catSpeed: GAME_CONSTANTS.INITIAL_CAT_SPEED,
        catScale: 1
      };
    default:
      return state;
  }
};

// Enhanced initial game state configuration
const initialGameState = {
  timeLeft: GAME_CONSTANTS.INITIAL_TIME,
  gameOver: false,
  gameRunning: true,
  gameOverReason: '',
  mousePos: { x: 50, y: 40 },
  catPos: { x: 280, y: 80 },
  cheeses: [],
  mouseDirection: 'right',
  catDirection: 'left',
  cheeseCounter: 0,
  totalCheeseCollected: 0,
  catSpeed: GAME_CONSTANTS.INITIAL_CAT_SPEED,
  catScale: 1
};

// Enhanced main game component with improved mobile deployment handling
const GameComponent = ({ setCurrentPage, targetCheese, deviceSettings }) => {
  const gameWidth = GAME_CONSTANTS.GAME_WIDTH;
  const gameHeight = GAME_CONSTANTS.GAME_HEIGHT;
  
  const [gameState, dispatch] = useReducer(gameReducer, initialGameState);
  
  const frameCountRef = useRef(0);
  const lastTimeRef = useRef(0);
  
  const mouseRef = useRef(null);
  const catRef = useRef(null);
  const positions = useRef({
    mouse: { x: 50, y: 40 },
    cat: { x: 280, y: 80 }
  });
  
  const keys = useRef({
    w: false, a: false, s: false, d: false
  });

  // Enhanced initial position setup with DOM update
  useEffect(() => {
    updateNodePos(mouseRef, positions.current.mouse.x, positions.current.mouse.y, gameWidth, gameHeight, true);
    updateNodePos(catRef, positions.current.cat.x, positions.current.cat.y, gameWidth, gameHeight, null, true);
  }, [gameWidth, gameHeight]);

  const catEmoji = useMemo(() => CAT_EMOJIS[gameState.catDirection] || CAT_EMOJIS.left, [gameState.catDirection]);

  // Enhanced cat transform style calculation for scaling optimization
  const catTransformStyle = useMemo(() => {
    const scale = gameState.catScale;
    return scale !== 1 
      ? `translate(-50%, -50%) scale(${scale})`
      : 'translate(-50%, -50%)';
  }, [gameState.catScale]);

  // Enhanced cheese creation with optimized placement using squared distance
  const createCheese = useCallback(() => {
    if (gameState.cheeses.length < GAME_CONSTANTS.MAX_CHEESE_COUNT && gameState.gameRunning) {
      let attempts = 0;
      let newCheese;
      let validPosition = false;
      
      while (!validPosition && attempts < GAME_CONSTANTS.MAX_PLACEMENT_ATTEMPTS) {
        const isDisappearing = deviceSettings.enableDisappearingCheese && 
                              gameState.timeLeft <= GAME_CONSTANTS.TIMER_THRESHOLD && 
                              Math.random() < GAME_CONSTANTS.DISAPPEARING_CHEESE_PROBABILITY;
        
        newCheese = {
          id: Date.now() + Math.random(),
          x: Math.random() * (gameWidth - GAME_CONSTANTS.ENTITY_BOUNDARY_LARGE) + GAME_CONSTANTS.ENTITY_BOUNDARY_SIZE,
          y: Math.random() * (gameHeight - GAME_CONSTANTS.ENTITY_BOUNDARY_LARGE) + GAME_CONSTANTS.ENTITY_BOUNDARY_SIZE,
          type: isDisappearing ? 'disappearing' : 'normal',
          ...(isDisappearing && { createdAt: Date.now() })
        };
        
        // Enhanced position validation using squared distance calculation
        const existingPositions = [
          ...gameState.cheeses.map(cheese => ({ x: cheese.x, y: cheese.y })),
          positions.current.mouse,
          positions.current.cat
        ];
        
        const minDistancesSquared = [
          ...gameState.cheeses.map(cheese => {
            if (!deviceSettings.enableDisappearingCheese) return THRESHOLDS.cheeseSpacing;
            return (newCheese.type === 'disappearing' && cheese.type === 'normal') || 
                   (newCheese.type === 'normal' && cheese.type === 'disappearing') ? 
                   THRESHOLDS.disappearingSpacing : THRESHOLDS.cheeseSpacing;
          }),
          THRESHOLDS.mouseDistance,
          THRESHOLDS.catDistance
        ];
        
        validPosition = isValidPosition(newCheese, existingPositions, minDistancesSquared);
        attempts++;
      }
      
      if (validPosition && newCheese) {
        dispatch({ 
          type: 'UPDATE_CHEESES', 
          cheeses: [...gameState.cheeses, newCheese] 
        });
      }
    }
  }, [gameState.cheeses, gameState.gameRunning, gameState.timeLeft, gameWidth, gameHeight, deviceSettings]);

  // Enhanced disappearing cheese check functionality
  const checkDisappearingCheeses = useCallback(() => {
    if (!deviceSettings.enableDisappearingCheese) return;
    
    const currentTime = Date.now();
    const filteredCheeses = gameState.cheeses.filter(cheese => {
      if (cheese.type === 'disappearing') {
        return currentTime - cheese.createdAt < GAME_CONSTANTS.DISAPPEARING_CHEESE_DURATION;
      }
      return true;
    });
    
    if (filteredCheeses.length !== gameState.cheeses.length) {
      dispatch({ type: 'UPDATE_CHEESES', cheeses: filteredCheeses });
    }
  }, [gameState.cheeses, deviceSettings.enableDisappearingCheese]);

  // Enhanced collision detection using optimized squared distance calculations
  const checkCollisions = useCallback(() => {
    let cheeseCollected = false;
    const remainingCheeses = gameState.cheeses.filter(cheese => {
      const distSquared = calculateDistanceSquared(positions.current.mouse, { x: cheese.x, y: cheese.y });
      
      if (distSquared < THRESHOLDS.cheeseCollect) {
        cheeseCollected = true;
        return false;
      }
      return true;
    });

    if (cheeseCollected) {
      dispatch({ 
        type: 'COLLECT_CHEESE', 
        cheeses: remainingCheeses,
        targetCheese: targetCheese
      });
    }

    // Enhanced cat catching mouse detection using squared distance
    const catDistSquared = calculateDistanceSquared(positions.current.mouse, positions.current.cat);
    
    if (catDistSquared < THRESHOLDS.catCatch) {
      dispatch({ type: 'GAME_OVER', reason: 'caught' });
    }
  }, [gameState.cheeses, targetCheese]);

  // Enhanced simple cat movement with squared distance optimization
  const moveCatSimple = useCallback(() => {
    if (!gameState.gameRunning) return;
    
    const distSquared = calculateDistanceSquared(positions.current.cat, positions.current.mouse);
    
    if (distSquared > 0) {
      const distance = Math.sqrt(distSquared);
      const dx = positions.current.mouse.x - positions.current.cat.x;
      const dy = positions.current.mouse.y - positions.current.cat.y;
      
      const moveX = (dx / distance) * gameState.catSpeed;
      const moveY = (dy / distance) * gameState.catSpeed;
      
      // Enhanced boundary calculation with dynamic scaling for mobile deployment
      const catBoundaryOffset = GAME_CONSTANTS.ENTITY_BOUNDARY_OFFSET * gameState.catScale;
      
      const newX = clamp(
        positions.current.cat.x + moveX,
        catBoundaryOffset,
        gameWidth - catBoundaryOffset
      );
      const newY = clamp(
        positions.current.cat.y + moveY,
        catBoundaryOffset,
        gameHeight - catBoundaryOffset
      );
      
      updateNodePos(catRef, newX, newY, gameWidth, gameHeight, null, true);
      positions.current.cat = { x: newX, y: newY };
      
      const newDirection = getCatDirection(moveX, moveY);
      
      if (newDirection !== gameState.catDirection) {
        dispatch({ 
          type: 'MOVE_CAT', 
          catPos: { x: newX, y: newY },
          direction: newDirection
        });
      }
    }
  }, [gameState.gameRunning, gameState.catDirection, gameState.catSpeed, gameState.catScale, gameWidth, gameHeight]);

  // Enhanced advanced cat movement with prediction and squared distance optimization
  const moveCatAdvanced = useCallback(() => {
    if (!gameState.gameRunning) return;
    
    const distSquared = calculateDistanceSquared(positions.current.cat, positions.current.mouse);
    
    if (distSquared > 0) {
      const distance = Math.sqrt(distSquared);
      const dx = positions.current.mouse.x - positions.current.cat.x;
      const dy = positions.current.mouse.y - positions.current.cat.y;
      
      let moveX, moveY;
      
      const mouseSpeed = GAME_CONSTANTS.MOUSE_SPEED;
      const catchTime = distance / gameState.catSpeed;
      
      let predictedMouseX = positions.current.mouse.x;
      let predictedMouseY = positions.current.mouse.y;
      
      // Enhanced key state prediction for advanced AI
      if (keys.current.w) predictedMouseY -= mouseSpeed * catchTime * GAME_CONSTANTS.PREDICTION_FACTOR;
      if (keys.current.s) predictedMouseY += mouseSpeed * catchTime * GAME_CONSTANTS.PREDICTION_FACTOR;
      if (keys.current.a) predictedMouseX -= mouseSpeed * catchTime * GAME_CONSTANTS.PREDICTION_FACTOR;
      if (keys.current.d) predictedMouseX += mouseSpeed * catchTime * GAME_CONSTANTS.PREDICTION_FACTOR;
      
      const predDistSquared = calculateDistanceSquared(positions.current.cat, { x: predictedMouseX, y: predictedMouseY });
      
      if (predDistSquared > 0) {
        const predDistance = Math.sqrt(predDistSquared);
        const predDx = predictedMouseX - positions.current.cat.x;
        const predDy = predictedMouseY - positions.current.cat.y;
        moveX = (predDx / predDistance) * gameState.catSpeed * GAME_CONSTANTS.CAT_ADVANCED_SPEED_MULTIPLIER;
        moveY = (predDy / predDistance) * gameState.catSpeed * GAME_CONSTANTS.CAT_ADVANCED_SPEED_MULTIPLIER;
      } else {
        moveX = (dx / distance) * gameState.catSpeed;
        moveY = (dy / distance) * gameState.catSpeed;
      }
      
      // Enhanced random movement behavior
      if (Math.random() < GAME_CONSTANTS.CAT_RANDOM_MOVE_PROBABILITY) {
        const angle = Math.atan2(dy, dx) + (Math.random() - 0.5) * Math.PI * 0.3;
        moveX = Math.cos(angle) * gameState.catSpeed * GAME_CONSTANTS.CAT_RANDOM_SPEED_MULTIPLIER;
        moveY = Math.sin(angle) * gameState.catSpeed * GAME_CONSTANTS.CAT_RANDOM_SPEED_MULTIPLIER;
      }
      
      // Enhanced boundary calculation with dynamic scaling for mobile deployment
      const catBoundaryOffset = GAME_CONSTANTS.ENTITY_BOUNDARY_OFFSET * gameState.catScale;
      
      const newX = clamp(
        positions.current.cat.x + moveX,
        catBoundaryOffset,
        gameWidth - catBoundaryOffset
      );
      const newY = clamp(
        positions.current.cat.y + moveY,
        catBoundaryOffset,
        gameHeight - catBoundaryOffset
      );
      
      updateNodePos(catRef, newX, newY, gameWidth, gameHeight, null, true);
      positions.current.cat = { x: newX, y: newY };
      
      const newDirection = getCatDirection(moveX, moveY);
      
      if (newDirection !== gameState.catDirection) {
        dispatch({ 
          type: 'MOVE_CAT', 
          catPos: { x: newX, y: newY },
          direction: newDirection
        });
      }
    }
  }, [gameState.gameRunning, gameState.catDirection, gameState.catSpeed, gameState.catScale, gameWidth, gameHeight]);

  const moveCat = deviceSettings.enableAdvancedAI ? moveCatAdvanced : moveCatSimple;

  // Enhanced mouse movement with optimized state updates
  const moveMouse = useCallback(() => {
    if (!gameState.gameRunning) return;
    
    const speed = GAME_CONSTANTS.MOUSE_SPEED;
    let newX = positions.current.mouse.x;
    let newY = positions.current.mouse.y;
    let direction = gameState.mouseDirection;
    
    // Enhanced WASD key state checking
    if (keys.current.w) {
      newY -= speed;
      direction = 'up';
    }
    if (keys.current.s) {
      newY += speed;
      direction = 'down';
    }
    if (keys.current.a) {
      newX -= speed;
      direction = 'left';
    }
    if (keys.current.d) {
      newX += speed;
      direction = 'right';
    }
    
    const hasMovement = newX !== positions.current.mouse.x || newY !== positions.current.mouse.y;
    
    if (hasMovement) {
      const flipX = direction === 'right';
      const newPos = moveAndUpdateNode(mouseRef, newX, newY, gameWidth, gameHeight, flipX);
      positions.current.mouse = newPos;
      
      if (direction !== gameState.mouseDirection) {
        dispatch({ 
          type: 'MOVE_MOUSE', 
          mousePos: newPos,
          direction: direction
        });
      }
    }
  }, [gameState.gameRunning, gameState.mouseDirection, gameWidth, gameHeight]);

  // Enhanced timer effect with better mobile deployment handling
  useEffect(() => {
    if (gameState.gameRunning && gameState.timeLeft > 0) {
      const timer = setTimeout(() => {
        const newTime = gameState.timeLeft - 1;
        
        if (newTime === GAME_CONSTANTS.TIMER_THRESHOLD || newTime === 0) {
          const k = GAME_CONSTANTS.CAT_SPEED_INCREMENT;
          const v_max = GAME_CONSTANTS.MAX_CAT_SPEED;
          const newCatSpeed = Math.min(v_max, gameState.catSpeed + k * gameState.cheeseCounter);
          dispatch({ type: 'UPDATE_CAT_SPEED', catSpeed: newCatSpeed });
          
          // Enhanced mobile scaling for better deployment compatibility
          if (newTime === GAME_CONSTANTS.TIMER_THRESHOLD && deviceSettings.isMobile && gameState.catScale === 1) {
            dispatch({ type: 'UPDATE_CAT_SCALE', catScale: GAME_CONSTANTS.CAT_SCALE_MOBILE });
          }
        }
        
        dispatch({ type: 'TIMER_TICK', timeLeft: newTime });
      }, GAME_CONSTANTS.TIMER_INTERVAL);
      return () => clearTimeout(timer);
    } else if (gameState.timeLeft === 0) {
      if (gameState.totalCheeseCollected < targetCheese) {
        dispatch({ type: 'GAME_OVER', reason: 'target_failed' });
      } else {
        dispatch({ type: 'GAME_OVER', reason: 'won' });
      }
    }
  }, [gameState.timeLeft, gameState.gameRunning, gameState.cheeseCounter, gameState.totalCheeseCollected, gameState.catSpeed, gameState.catScale, targetCheese, deviceSettings.isMobile]);

  // Enhanced game loop with better mobile deployment performance
  useEffect(() => {
    let rafId;
    const targetFPS = GAME_CONSTANTS.TARGET_FPS;
    const frameInterval = GAME_CONSTANTS.TIMER_INTERVAL / targetFPS;
    
    const gameLoop = (currentTime) => {
      if (!gameState.gameRunning) return;
      
      if (currentTime - lastTimeRef.current >= frameInterval) {
        moveMouse();
        moveCat();
        checkCollisions();
        
        if (deviceSettings.enableDisappearingCheese) {
          checkDisappearingCheeses();
        }
        
        if (frameCountRef.current % 60 === 0 && Math.random() < GAME_CONSTANTS.CHEESE_CREATION_PROBABILITY) {
          createCheese();
        }
        frameCountRef.current += 1;
        
        lastTimeRef.current = currentTime;
      }
      
      rafId = requestAnimationFrame(gameLoop);
    };
    
    rafId = requestAnimationFrame(gameLoop);
    return () => cancelAnimationFrame(rafId);
  }, [gameState.gameRunning, moveMouse, moveCat, checkCollisions, ...(deviceSettings.enableDisappearingCheese ? [checkDisappearingCheeses] : []), createCheese]);

  // Enhanced initial cheese creation effect
  useEffect(() => {
    if (gameState.gameRunning && gameState.cheeses.length === 0) {
      createCheese();
    }
  }, [gameState.gameRunning, gameState.cheeses.length, createCheese]);

  // Enhanced game restart functionality
  const restartGame = useCallback(() => {
    positions.current = {
      mouse: { x: 50, y: 40 },
      cat: { x: 280, y: 80 }
    };
    
    updateNodePos(mouseRef, positions.current.mouse.x, positions.current.mouse.y, gameWidth, gameHeight, true);
    updateNodePos(catRef, positions.current.cat.x, positions.current.cat.y, gameWidth, gameHeight, null, true);
    
    dispatch({ type: 'RESTART_GAME' });
    frameCountRef.current = 0;
    lastTimeRef.current = 0;
  }, [gameWidth, gameHeight]);

  // Enhanced keyboard event handling with better mobile deployment support
  useEffect(() => {
    const handleKeyboard = (e) => {
      let mappedKey = null;
      
      // Enhanced key mapping for consistency across deployments
      switch(e.key) {
        case 'w':
        case 'W':
        case 'ArrowUp':
          mappedKey = 'w';
          break;
        case 's':
        case 'S':
        case 'ArrowDown':
          mappedKey = 's';
          break;
        case 'a':
        case 'A':
        case 'ArrowLeft':
          mappedKey = 'a';
          break;
        case 'd':
        case 'D':
        case 'ArrowRight':
          mappedKey = 'd';
          break;
        default:
          break;
      }
      
      if (mappedKey) {
        e.preventDefault();
        keys.current[mappedKey] = e.type === 'keydown';
      }
    };

    window.addEventListener('keydown', handleKeyboard);
    window.addEventListener('keyup', handleKeyboard);
    
    return () => {
      window.removeEventListener('keydown', handleKeyboard);
      window.removeEventListener('keyup', handleKeyboard);
    };
  }, []);

  // Enhanced touch button handler with better mobile deployment support
  const handleTouchButton = useCallback((direction, isPressed) => {
    switch(direction) {
      case 'up':
        keys.current.w = isPressed;
        break;
      case 'down':
        keys.current.s = isPressed;
        break;
      case 'left':
        keys.current.a = isPressed;
        break;
      case 'right':
        keys.current.d = isPressed;
        break;
      default:
        break;
    }
  }, []);

  return (
    <>
      <style jsx global>{`
        .game-container {
          user-select: none;
          touch-action: none;
        }
        .game-container * {
          user-select: inherit;
          touch-action: inherit;
        }
        
        @keyframes alertFlash {
          0%, 100% {
            border-color: #dc2626;
            box-shadow: 0 0 4px rgba(220, 38, 38, 0.4);
          }
          50% {
            border-color: #fff;
            box-shadow: 
              0 0 8px 2px rgba(255, 255, 255, 0.9),
              0 0 16px 4px rgba(220, 38, 38, 0.8);
          }
        }
        
        @keyframes cheeseDisappearing {
          0%, 100% {
            opacity: 1;
            filter: brightness(1);
          }
          50% {
            opacity: 0.3;
            filter: brightness(2);
          }
        }
        
        @keyframes cheeseBorderMove {
          0% {
            background-position: 100% 0%;
          }
          100% {
            background-position: 0% 100%;
          }
        }
        
        .game-area {
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, #86efac, #4ade80);
          border: 2px solid #064e3b;
          border-radius: 16px;
        }
        
        .game-area.alert {
          animation: alertFlash 0.6s ease-in-out infinite;
          border-color: #dc2626;
        }
        
        .cat-scaling {
          transition: transform 1.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }
        
        .font-inter {
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
        }
        
        @media (max-width: 768px) {
          .font-inter {
            font-family: system-ui, -apple-system, BlinkMacSystemFont, sans-serif;
          }
        }
      `}</style>
      
      <div className="min-h-screen bg-gradient-to-br from-green-400 via-green-500 to-green-600 p-2 flex flex-col"
           style={{ minHeight: '100vh', minHeight: '-webkit-fill-available' }}>
        <div className="flex-1 flex flex-col items-center justify-center max-w-sm mx-auto w-full">
          
          <Header 
            timeLeft={gameState.timeLeft}
            totalCheeseCollected={gameState.totalCheeseCollected}
            targetCheese={targetCheese}
            setCurrentPage={setCurrentPage}
            deviceSettings={deviceSettings}
          />

          <div className="relative w-full mb-4" style={{ height: `${gameHeight}px` }}>
            
            <div 
              className={`game-area shadow-2xl ${gameState.timeLeft <= 7 ? 'alert' : ''}`}
            />
            
            <div 
              className="absolute inset-0 overflow-hidden"
              style={{
                borderRadius: `${GAME_CONSTANTS.RADIUS - GAME_CONSTANTS.BORDER_WIDTH}px`,
                margin: `${GAME_CONSTANTS.BORDER_WIDTH}px`
              }}
            >
              
              <div 
                ref={mouseRef}
                className="text-xl flex items-center justify-center w-6 h-6 pointer-events-none z-10"
                style={{
                  position: 'absolute',
                  left: `${(50 / gameWidth) * 100}%`,
                  top: `${(40 / gameHeight) * 100}%`,
                  transform: 'translate(-50%, -50%) scaleX(-1)'
                }}
              >
                {MOUSE_EMOJI}
              </div>

              <div 
                ref={catRef}
                className={`flex items-center justify-center w-7 h-7 pointer-events-none z-20 ${
                  deviceSettings.isMobile ? 'cat-scaling' : ''
                }`}
                style={{
                  position: 'absolute',
                  left: `${(280 / gameWidth) * 100}%`,
                  top: `${(80 / gameHeight) * 100}%`,
                  transform: catTransformStyle,
                  fontSize: '22px'
                }}
              >
                {catEmoji}
              </div>

              {gameState.cheeses.map(cheese => {
                const isDisappearing = deviceSettings.enableDisappearingCheese && cheese.type === 'disappearing';
                
                const cheeseStyle = {
                  position: 'absolute',
                  left: `${(cheese.x / gameWidth) * 100}%`,
                  top: `${(cheese.y / gameHeight) * 100}%`,
                  transform: 'translate(-50%, -50%)',
                  ...(isDisappearing && {
                    animation: 'cheeseDisappearing 0.5s ease-in-out infinite',
                    filter: 'hue-rotate(180deg) saturate(0) brightness(2)'
                  })
                };

                return (
                  <div
                    key={cheese.id}
                    className={`absolute text-sm flex items-center justify-center w-4 h-4 pointer-events-none z-10 ${
                      cheese.type === 'normal' ? 'animate-bounce' : ''
                    }`}
                    style={cheeseStyle}
                  >
                    🧀
                  </div>
                );
              })}

              {gameState.gameOver && (
                <div style={staticStyles.gameOverlay}>
                  <div style={staticStyles.gameOverModal}>
                    {gameState.gameOverReason === 'won' ? (
                      <>
                        <h2 className="text-lg font-bold text-gray-800 mb-2">
                          <span>🏆</span> Congratulations!
                        </h2>
                        <p className="text-base text-gray-600 mb-3">
                          You reached the target!
                        </p>
                      </>
                    ) : gameState.gameOverReason === 'caught' ? (
                      <>
                        <h2 className="text-lg font-bold text-gray-800 mb-2">
                          <span>❌</span> Game Over!
                        </h2>
                        <p className="text-base text-gray-600 mb-3">
                          You failed, the cat got you!
                        </p>
                      </>
                    ) : (
                      <>
                        <h2 className="text-lg font-bold text-gray-800 mb-2">
                          <span>⌛</span> Time's Up!
                        </h2>
                        <p className="text-base text-gray-600 mb-3">
                          Target failed! Collected <span>{gameState.totalCheeseCollected}</span> cheese.
                        </p>
                      </>
                    )}
                    <button 
                      onClick={restartGame}
                      className="bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded-lg transition-colors"
                      style={touchButtonBaseStyle}
                    >
                      <span>🔄 Play Again</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="w-full bg-green-700 rounded-xl p-4">
            <div className="text-left mb-1">
              <p className="text-white/90 text-sm font-medium">Controls</p>
            </div>
            
            <div className="grid grid-cols-3 gap-3 max-w-xs mx-auto">
              <div></div>
              <TouchButton 
                direction="up" 
                onPress={handleTouchButton}
                className="mx-auto"
              />
              <div></div>
              
              <div className="col-span-3 flex justify-center items-center gap-20">
                <TouchButton 
                  direction="left" 
                  onPress={handleTouchButton}
                />
                <TouchButton 
                  direction="right" 
                  onPress={handleTouchButton}
                />
              </div>
              
              <div></div>
              <TouchButton 
                direction="down" 
                onPress={handleTouchButton}
                className="mx-auto"
              />
              <div></div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// Enhanced main component with improved device detection for mobile deployment
const TheRelentlessCheeseHunt = () => {
  const [currentPage, setCurrentPage] = useState('landing');
  
  const [deviceSettings] = useState(() => {
    // Enhanced mobile detection for better deployment compatibility
    const isMobile = navigator.userAgentData?.mobile ?? /Mobi|Android/i.test(navigator.userAgent);
    return {
      isMobile: isMobile,
      targetCheese: isMobile ? GAME_CONSTANTS.TARGET_CHEESE_MOBILE : GAME_CONSTANTS.TARGET_CHEESE_DESKTOP,
      enableDisappearingCheese: !isMobile,
      enableAdvancedAI: !isMobile
    };
  });
  
  return (
    <div className="game-container">
      {currentPage === 'landing' ? 
        <LandingPage 
          setCurrentPage={setCurrentPage} 
          targetCheese={deviceSettings.targetCheese}
          isMobile={deviceSettings.isMobile}
        /> : 
        <GameComponent 
          setCurrentPage={setCurrentPage}
          targetCheese={deviceSettings.targetCheese}
          deviceSettings={deviceSettings}
        />
      }
    </div>
  );
};

// Enhanced app component wrapper for proper React app structure
function App() {
  return <TheRelentlessCheeseHunt />;
}

export default App;
